import React, { useState, useCallback, useEffect, useRef } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Paper,
  TextField,
  Typography,
  Chip,
  Grid,
  LinearProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import { DeleteForever, DeleteSweep } from '@mui/icons-material';
import DashboardLayout from '../components/DashboardLayout';
import { useAppDispatch } from '../store';
import { showNotification } from '../store/notificationSlice';
import { apiService } from '../services/api';
import type { TaskResponse } from '../types/timekeeping';

type ConfirmMode = 'single' | 'all' | null;

const POLL_INTERVAL_MS = 4000;
const TASK_TIMEOUT_MS = 15 * 60 * 1000;
const TERMINAL_STATUSES = new Set(['COMPLETED', 'FAILED', 'CANCELLED', 'WARNING']);

function needsPolling(t: TaskResponse): boolean {
  if (TERMINAL_STATUSES.has(t.status)) return false;
  return Date.now() - new Date(t.createdAt).getTime() < TASK_TIMEOUT_MS;
}

function statusColor(status: string): 'success' | 'error' | 'warning' | 'info' | 'default' {
  if (status === 'COMPLETED') return 'success';
  if (status === 'FAILED') return 'error';
  if (status === 'WARNING') return 'warning';
  if (status === 'PROCESSING' || status === 'PENDING') return 'info';
  return 'default';
}

export default function MaintenanceSystemStates() {
  const dispatch = useAppDispatch();

  const [uuidInput, setUuidInput] = useState('');
  const [confirmMode, setConfirmMode] = useState<ConfirmMode>(null);
  const [confirmText, setConfirmText] = useState('');
  const [loading, setLoading] = useState(false);
  const [dialogError, setDialogError] = useState<string | null>(null);

  // Active employee snapshots state
  const [initiatedSnapshots, setInitiatedSnapshots] = useState<string[]>([]);
  const [loadingSnapshots, setLoadingSnapshots] = useState(false);
  const [targetMonth, setTargetMonth] = useState('');
  const [generating, setGenerating] = useState(false);
  const [generatingMethod, setGeneratingMethod] = useState<'baseline' | 'incremental' | null>(null);

  // Snapshot Tasks State
  const [tasks, setTasks] = useState<TaskResponse[]>([]);
  const [tasksLoading, setTasksLoading] = useState<boolean>(true);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const loadInitiatedSnapshots = useCallback(async () => {
    setLoadingSnapshots(true);
    try {
      const res = await apiService.getActiveEmployeeSnapshots();
      setInitiatedSnapshots(res.data || []);
    } catch (err: any) {
      dispatch(showNotification({ message: err?.message || 'Failed to fetch snapshots', severity: 'error' }));
    } finally {
      setLoadingSnapshots(false);
    }
  }, [dispatch]);

  const fetchSnapshotTasks = useCallback(async () => {
    try {
      const res = await apiService.listTasks('ACTIVE_EMPLOYEE_SNAPSHOT');
      const updated = res.data ?? [];
      setTasks(updated);
      return updated;
    } catch (err: any) {
      console.error("Task list retrieval failed:", err);
      return [];
    } finally {
      setTasksLoading(false);
    }
  }, []);

  const startPolling = useCallback((currentTasks: TaskResponse[]) => {
    if (pollRef.current) clearInterval(pollRef.current);
    if (!currentTasks.some(needsPolling)) return;

    pollRef.current = setInterval(async () => {
      try {
        const res = await apiService.listTasks('ACTIVE_EMPLOYEE_SNAPSHOT');
        const updated = res.data ?? [];
        setTasks(updated);
        if (!updated.some(needsPolling)) {
          if (pollRef.current) clearInterval(pollRef.current);
          loadInitiatedSnapshots();
        }
      } catch {
        // Retry next cycle
      }
    }, POLL_INTERVAL_MS);
  }, [loadInitiatedSnapshots]);

  useEffect(() => {
    loadInitiatedSnapshots();
  }, [loadInitiatedSnapshots]);

  useEffect(() => {
    fetchSnapshotTasks().then(startPolling);
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [fetchSnapshotTasks, startPolling]);

  const handleGenerate = useCallback(async (method: 'baseline' | 'incremental') => {
    if (!targetMonth) return;
    setGenerating(true);
    setGeneratingMethod(method);
    try {
      let res;
      if (method === 'baseline') {
        res = await apiService.generateActiveEmployeeBaselineSnapshot(targetMonth);
      } else {
        res = await apiService.generateActiveEmployeeIncrementalSnapshot(targetMonth);
      }
      dispatch(showNotification({ message: res.data?.message || res.message || 'Snapshot generation task scheduled', severity: 'success' }));
      const updated = await fetchSnapshotTasks();
      startPolling(updated);
    } catch (err: any) {
      dispatch(showNotification({ message: err?.message || 'Failed to generate snapshot', severity: 'error' }));
    } finally {
      setGenerating(false);
      setGeneratingMethod(null);
    }
  }, [targetMonth, dispatch, fetchSnapshotTasks, startPolling]);

  const openConfirm = useCallback((mode: ConfirmMode) => {
    setConfirmMode(mode);
    setConfirmText('');
    setDialogError(null);
  }, []);

  const closeConfirm = useCallback(() => {
    setConfirmMode(null);
    setConfirmText('');
    setDialogError(null);
  }, []);

  const handleConfirm = useCallback(async () => {
    setLoading(true);
    setDialogError(null);
    try {
      if (confirmMode === 'single') {
        const res = await apiService.deleteFrontendCacheByUUID(uuidInput.trim());
        dispatch(showNotification({ message: res.data.message, severity: 'success' }));
        setUuidInput('');
        closeConfirm();
      } else if (confirmMode === 'all') {
        const res = await apiService.deleteAllFrontendCaches();
        dispatch(showNotification({
          message: `${res.data.message} (${res.data.count} entries)`,
          severity: 'success',
        }));
        closeConfirm();
      }
    } catch (err: any) {
      const status = err?.status ?? err?.response?.status;
      if (status === 404) {
        dispatch(showNotification({ message: 'UUID not found in cache', severity: 'warning' }));
        closeConfirm();
      } else {
        setDialogError(err?.message ?? 'Operation failed');
      }
    } finally {
      setLoading(false);
    }
  }, [confirmMode, uuidInput, dispatch, closeConfirm]);

  const confirmWord = 'delete';
  const confirmReady = confirmText.toLowerCase() === confirmWord;

  const isSnapshotRunning = tasks.some(needsPolling);
  const activeTask = tasks.find(needsPolling);

  return (
    <DashboardLayout>
      <Box sx={{ p: 3, maxWidth: 900 }}>
        <Typography variant="h5" fontWeight={600} sx={{ mb: 3 }}>
          System States
        </Typography>

        <Paper variant="outlined">
          <Box sx={{ p: 2.5 }}>
            <Typography variant="subtitle1" fontWeight={600}>
              Frontend Cache
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Each browser-unit pair holds a UUID in localStorage that is validated on every load.
              Deleting a UUID forces that browser to wipe its local employee cache and re-sync on
              next visit.
            </Typography>
          </Box>

          <Divider />

          {/* Clear specific UUID */}
          <Box sx={{ p: 2.5, display: 'flex', alignItems: 'center', gap: 2 }}>
            <TextField
              label="Cache UUID"
              size="small"
              value={uuidInput}
              onChange={(e) => setUuidInput(e.target.value)}
              sx={{ flex: 1, fontFamily: 'monospace' }}
              inputProps={{ style: { fontFamily: 'monospace' } }}
            />
            <Button
              variant="outlined"
              color="warning"
              startIcon={<DeleteForever />}
              disabled={uuidInput.trim() === ''}
              onClick={() => openConfirm('single')}
            >
              Clear
            </Button>
          </Box>

          <Divider />

          {/* Clear all */}
          <Box sx={{ p: 2.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box>
              <Typography variant="body2" fontWeight={500}>
                Clear all frontend caches
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Forces every browser-unit to re-sync on next load.
              </Typography>
            </Box>
            <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteSweep />}
              onClick={() => openConfirm('all')}
            >
              Clear All
            </Button>
          </Box>
        </Paper>

        {/* Monthly Active Employee Snapshots Card */}
        <Paper variant="outlined" sx={{ mt: 3 }}>
          <Box sx={{ p: 2.5 }}>
            <Typography variant="subtitle1" fontWeight={600}>
              Monthly Active Employee Snapshots
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Maintains a lightweight snapshot of active employees for each month to optimize reporting efficiency and reduce DynamoDB read costs.
            </Typography>
          </Box>

          <Divider />

          {/* List initiated snapshots */}
          <Box sx={{ p: 2.5 }}>
            <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
              Recently Initiated Snapshots
            </Typography>
            {loadingSnapshots ? (
              <CircularProgress size={20} />
            ) : initiatedSnapshots.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No snapshots generated yet.
              </Typography>
            ) : (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {initiatedSnapshots.map((m) => (
                  <Chip
                    key={m}
                    label={m}
                    color="primary"
                    variant="outlined"
                    size="small"
                  />
                ))}
              </Box>
            )}
          </Box>

          <Divider />

          {/* Generate snapshot form */}
          <Box sx={{ p: 2.5 }}>
            <Typography variant="body2" fontWeight={600} sx={{ mb: 2 }}>
              Generate Monthly Snapshot
            </Typography>

            <Grid container spacing={2} alignItems="center">
              <Grid size={{ xs: 12, sm: 4 }}>
                <TextField
                  label="Target Month"
                  type="month"
                  size="small"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  value={targetMonth}
                  onChange={(e) => setTargetMonth(e.target.value)}
                  disabled={generating || isSnapshotRunning}
                />
              </Grid>

              <Grid size={{ xs: 12, sm: 8 }} sx={{ display: 'flex', gap: 1.5 }}>
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={() => handleGenerate('baseline')}
                  disabled={!targetMonth || generating || isSnapshotRunning}
                  fullWidth
                >
                  {generatingMethod === 'baseline' ? (
                    <CircularProgress size={18} sx={{ mr: 1 }} />
                  ) : null}
                  Baseline (From Scratch)
                </Button>

                <Button
                  variant="outlined"
                  color="secondary"
                  onClick={() => handleGenerate('incremental')}
                  disabled={!targetMonth || generating || isSnapshotRunning}
                  fullWidth
                >
                  {generatingMethod === 'incremental' ? (
                    <CircularProgress size={18} sx={{ mr: 1 }} />
                  ) : null}
                  Incremental (From Previous)
                </Button>
              </Grid>
            </Grid>

            {activeTask && (
              <Paper variant="outlined" sx={{ p: 2, mt: 3, bgcolor: 'action.hover' }}>
                <Stack spacing={1}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="body2" fontWeight={600}>
                      Running Task Progress ({activeTask.progressPercent}%)
                    </Typography>
                    <Chip size="small" label={activeTask.status} color="info" />
                  </Stack>
                  <LinearProgress variant="determinate" value={activeTask.progressPercent} sx={{ height: 6, borderRadius: 1 }} />
                  <Typography variant="caption" color="text.secondary" sx={{ wordBreak: 'break-word' }}>
                    Log: {activeTask.logs?.at(-1) ?? 'Initializing snapshot generation...'}
                  </Typography>
                </Stack>
              </Paper>
            )}
          </Box>
        </Paper>

        {/* Snapshot Tasks History */}
        <Typography variant="h6" fontWeight={600} sx={{ mt: 4, mb: 1.5 }}>
          Snapshot Tasks History
        </Typography>
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: 'action.hover' }}>
                <TableCell sx={{ fontWeight: 600 }}>Task ID / Label</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Status Message</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Started At</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {tasksLoading ? (
                <TableRow>
                  <TableCell colSpan={4} align="center" sx={{ py: 3 }}>
                    <CircularProgress size={24} sx={{ mr: 1, verticalAlign: 'middle' }} />
                    Loading task history...
                  </TableCell>
                </TableRow>
              ) : tasks.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} align="center" sx={{ py: 3, color: 'text.secondary' }}>
                    No snapshot tasks found.
                  </TableCell>
                </TableRow>
              ) : (
                tasks.map((t) => {
                  const lastLog = t.logs?.at(-1) ?? '';
                  return (
                    <TableRow key={t.taskId} hover>
                      <TableCell sx={{ fontWeight: 500 }}>
                        {t.label || t.taskId.slice(0, 8)}
                      </TableCell>
                      <TableCell>
                        <Chip label={t.status} color={statusColor(t.status)} size="small" variant="outlined" />
                      </TableCell>
                      <TableCell sx={{ maxWidth: 280, wordBreak: 'break-word', color: 'text.secondary', fontSize: '0.8rem' }}>
                        {lastLog}
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap', color: 'text.secondary', fontSize: '0.8rem' }}>
                        {new Date(t.createdAt).toLocaleString()}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      {/* Confirmation dialog */}
      <Dialog open={confirmMode !== null} onClose={closeConfirm} maxWidth="xs" fullWidth>
        <DialogTitle>Confirm Reset</DialogTitle>
        <DialogContent>
          {dialogError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {dialogError}
            </Alert>
          )}
          <Typography variant="body2" sx={{ mb: 2 }}>
            {confirmMode === 'single'
              ? `This will invalidate the frontend cache for UUID "${uuidInput.trim()}". The affected browser will perform a full re-sync on next load.`
              : 'This will invalidate all frontend caches. Every browser-unit will perform a full re-sync on next load.'}
          </Typography>
          <TextField
            autoFocus
            label={`Type "${confirmWord}" to confirm`}
            size="small"
            fullWidth
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            disabled={loading}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={closeConfirm} disabled={loading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleConfirm}
            disabled={!confirmReady || loading}
          >
            {loading ? <CircularProgress size={18} sx={{ mr: 1 }} /> : null}
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
}
